import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const fmt = (n, dp = 0) => Number.isFinite(+n) ? Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) : "—";

const badge = (s) => ({
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
}[s] || "bg-slate-500/15 text-slate-400");

export default function TransactionsList() {
  const { t, user } = useApp();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    const params = {};
    if (status && status !== "all") params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/transactions", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, from, to]);

  const del = async (tx) => {
    if (!confirm(`Hapus transaksi ${tx.tx_number}? / Delete transaction ${tx.tx_number}?`)) return;
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success(t("delete"));
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t("all_transactions")}</h1>

      <Card className="p-4 glass border-slate-800 flex flex-wrap gap-3 items-end">
        <div>
          <div className="text-xs text-slate-400 mb-1">{t("status")}</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="completed">{t("completed")}</SelectItem>
              <SelectItem value="pending">{t("pending")}</SelectItem>
              <SelectItem value="cancelled">{t("cancelled")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-1">From</div>
          <Input data-testid="filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-1">To</div>
          <Input data-testid="filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" />
        </div>
      </Card>

      <Card className="glass border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead>{t("tx_number")}</TableHead>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("customer")}</TableHead>
              <TableHead>Chain</TableHead>
              <TableHead className="text-right">Amount In</TableHead>
              <TableHead className="text-right">{t("profit")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>Teller</TableHead>
              {isAdmin && <TableHead className="text-right">{t("delete")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-slate-500 py-8">No transactions</TableCell></TableRow>
            )}
            {items.map((tx) => {
              const first = tx.legs?.[0];
              const last = tx.legs?.[tx.legs.length - 1];
              return (
                <TableRow key={tx.id} data-testid={`tx-row-${tx.tx_number}`} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell className="font-mono text-xs text-amber-400">{tx.tx_number}</TableCell>
                  <TableCell className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleString()}</TableCell>
                  <TableCell>{tx.customer_name || "Walk-in"}</TableCell>
                  <TableCell className="text-xs">
                    {tx.legs?.map((l, i) => (
                      <span key={i} className="mr-1">
                        {l.from_currency}
                        {i === tx.legs.length - 1 ? `→${l.to_currency}` : "→"}
                      </span>
                    ))}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmt(first?.from_amount, 2)} {first?.from_currency}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${tx.total_profit_idr >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {fmt(tx.total_profit_idr, 0)}
                  </TableCell>
                  <TableCell>
                    <span className={`pill border ${badge(tx.status)}`}>{t(tx.status) || tx.status}</span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">{tx.created_by_name}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <button
                        data-testid={`tx-del-${tx.tx_number}`}
                        onClick={() => del(tx)}
                        className="text-slate-500 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
